import { AlertCircle, Gauge, Info, RefreshCw, TrendingUp } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { MonthlyUsageTable } from "../components/rate-providers/MonthlyUsageTable";
import { ProviderErrorsPanel } from "../components/rate-providers/ProviderErrorsPanel";
import { ProviderPriorityLadder } from "../components/rate-providers/ProviderPriorityLadder";
import { buildMonthOptions, useRateProviderUsage } from "../hooks/useRateProviderUsage";

/**
 * `/app/payments-config/rate-providers` — how much of each FX rate provider's monthly quota we spent, for
 * INTERNAL staff.
 *
 * Provider codes are rendered verbatim here, which the rest of the dashboard never does (rule 12). The
 * exception is deliberate and bounded: the endpoint is `userTypes: [INTERNAL]`, so no organization member
 * reaches this page, and the page's whole job is reconciling spend against one vendor's contract — a
 * Coloca-branded label would make the number unusable for the person holding the invoice.
 *
 * Freshness: no polling. The counter only moves when a rate is refreshed, and this is a page staff open to
 * read a month, not a live feed.
 */
const RateProviderUsagePage = () => {
  const { t, activeLanguage } = useTranslations("rateProviderUsage");
  const { t: tGlobal } = useTranslations("global");

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_rate_providers"), to: "/app/payments-config/rate-providers" },
      ],
      [tGlobal],
    ),
  );

  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [selectedValue, setSelectedValue] = useState(monthOptions[0].value);
  const selectedMonth = useMemo(
    () => monthOptions.find((option) => option.value === selectedValue) ?? monthOptions[0],
    [monthOptions, selectedValue],
  );

  const state = useRateProviderUsage(selectedMonth);

  const formatCount = useCallback(
    (value: number) => new Intl.NumberFormat(activeLanguage).format(value),
    [activeLanguage],
  );

  const formatMonth = useCallback(
    (month: string) => {
      const [year, index] = month.split("-");
      const date = new Date(Date.UTC(Number(year), Number(index) - 1, 1));
      return new Intl.DateTimeFormat(activeLanguage, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);
    },
    [activeLanguage],
  );

  const formatPercent = useCallback(
    (ratio: number) =>
      new Intl.NumberFormat(activeLanguage, { style: "percent", maximumFractionDigits: 0 }).format(ratio),
    [activeLanguage],
  );

  const formatDateTime = useCallback(
    (iso: string) =>
      new Intl.DateTimeFormat(activeLanguage, { dateStyle: "medium", timeStyle: "short" }).format(
        new Date(iso),
      ),
    [activeLanguage],
  );

  const monthOptionItems = useMemo(
    () => monthOptions.map((option) => ({ value: option.value, label: formatMonth(option.value) })),
    [monthOptions, formatMonth],
  );

  const providers = state.month?.providers ?? [];
  const monthTotal = providers.reduce((sum, provider) => sum + provider.total, 0);
  const monthFailed = providers.reduce((sum, provider) => sum + provider.failed, 0);
  const primaryCode = state.month?.configured_priority[0] ?? null;

  if (!state.hasAccess) return <NoAccessView />;

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <ColocaButton
            variant="default"
            size="sm"
            icon={RefreshCw}
            aria-label={t("refresh_aria")}
            className={cn(state.isRefreshing && "[&_svg]:animate-spin")}
            onClick={state.refresh}
          >
            {t("refresh_button")}
          </ColocaButton>
        }
      />

      <ColocaAlert variant="secondary" icon={Info} heading={t("why_heading")} text={t("why_text")} />

      <div className="flex max-w-xs flex-col gap-2">
        <ColocaLabel htmlFor="rate-provider-usage-month" className="text-xs text-muted-foreground">
          {t("month_label")}
        </ColocaLabel>
        <ColocaCombobox
          id="rate-provider-usage-month"
          value={selectedValue}
          // A cleared combobox must not widen the window to "all time": the plan is monthly, so an empty
          // selection falls back to the running month rather than to an unbounded report.
          onValueChange={(value) => setSelectedValue(value ?? monthOptions[0].value)}
          options={monthOptionItems}
          aria-label={t("month_label")}
        />
      </div>

      {state.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={state.refresh}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : state.isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
            ))}
          </div>
          <ColocaSkeleton className="h-64 rounded-xl" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ColocaKpiCard
              icon={Gauge}
              accent="primary"
              label={t("kpi_month_requests")}
              value={formatCount(monthTotal)}
              meta={t("kpi_month_requests_meta", { month: formatMonth(selectedMonth.value) })}
            />
            <ColocaKpiCard
              icon={AlertCircle}
              accent={monthFailed > 0 ? "danger" : "neutral"}
              label={t("kpi_month_failed")}
              value={formatCount(monthFailed)}
              meta={t("kpi_month_failed_meta")}
            />
            <ColocaKpiCard
              icon={TrendingUp}
              accent="info"
              // The configured primary is a provider code, not a translatable label.
              label={t("kpi_primary")}
              value={primaryCode ?? t("kpi_primary_none")}
              meta={t("kpi_primary_meta")}
            />
          </div>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">{t("ladder_heading")}</h2>
              <p className="text-sm text-muted-foreground">{t("ladder_description")}</p>
            </div>
            {providers.length > 0 ? (
              <ProviderPriorityLadder
                providers={providers}
                formatCount={formatCount}
                formatPercent={formatPercent}
              />
            ) : (
              <ColocaEmpty title={t("empty_title")} description={t("empty_description")} />
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">{t("monthly_heading")}</h2>
              <p className="text-sm text-muted-foreground">{t("monthly_description")}</p>
            </div>
            {state.trend.length > 0 ? (
              <MonthlyUsageTable
                months={state.trend}
                providerCodes={state.providerCodes}
                selectedMonth={selectedMonth.value}
                formatCount={formatCount}
                formatMonth={formatMonth}
              />
            ) : (
              <ColocaEmpty title={t("monthly_empty_title")} description={t("monthly_empty_description")} />
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">{t("errors_heading")}</h2>
              <p className="text-sm text-muted-foreground">{t("errors_description")}</p>
            </div>
            <ProviderErrorsPanel
              providers={providers}
              formatCount={formatCount}
              formatDateTime={formatDateTime}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default RateProviderUsagePage;
